/**
 * TotalsRow — A sticky footer row for CRM tables showing per-column aggregations.
 *
 * - Left (pinned) pane: shows the total row count.
 * - Right (scrollable) pane: shows an AggregationPicker for each property column.
 * - Selections are persisted to localStorage and validated against the current schema.
 *
 * Integrates with the renderFooter prop of DataTable via FooterRenderContext.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import type { DataTableColumnConfig } from '@/lib/base/table/types';
import { useOrgTimeFormat } from '@/lib/time';
import type { CrmObjectFilters } from '@/types/crm';

import type { PropertySchemaMap } from '../../hooks/usePropertySchema';
import type { AggregationFunction } from '../hooks/useColumnAggregations';
import { useColumnAggregations } from '../hooks/useColumnAggregations';
import { AggregationPicker } from './AggregationPicker';
import {
  formatAggregationValue,
  loadSelectionsFromStorage,
  validateSelections,
} from './TotalsRow.utils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TotalsRowProps {
  /** Which pane this footer is being rendered in */
  pane: 'left' | 'right';
  /** Columns pinned to the left pane (from FooterRenderContext) */
  leftColumns: DataTableColumnConfig[];
  /** Columns in the scrollable right pane (from FooterRenderContext) */
  rightColumns: DataTableColumnConfig[];
  /** Pre-computed pixel widths for each column, keyed by column ID */
  columnWidths: Record<string, number>;
  /** Total content width of the right pane (for scroll alignment) */
  rightPaneContentWidth: number;
  /** Extra width for add-column button area (matches header/body scroll extent) */
  addColumnExtraWidth: number;
  /** Total number of rows in the table */
  rowCount: number;
  /** Org ID for aggregation API calls */
  orgId: number;
  /** Entity type name for aggregation API calls */
  entityTypeName: string;
  /** localStorage key for persisting column aggregation selections */
  storageKey: string;
  /** Schema map: property_type_name -> schema */
  schema: PropertySchemaMap;
  /** Map of field_type_name -> list of supported aggregation functions */
  fieldTypeAggregations: Record<string, string[]>;
  /** Current filters to apply to aggregation queries */
  filters?: CrmObjectFilters;
  /** Org default currency; when set, aggregations are converted server-side to a single amount. Omitted → per-currency display. */
  convertTo?: string;
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

/**
 * TotalsRow renders a sticky footer row below the CRM table with:
 * - Row count in the left (pinned) pane
 * - AggregationPicker for each property column in the right (scrollable) pane
 */
export function TotalsRow({
  pane,
  leftColumns: _leftColumns,
  rightColumns,
  columnWidths,
  rightPaneContentWidth,
  addColumnExtraWidth,
  rowCount,
  orgId,
  entityTypeName,
  storageKey,
  schema,
  fieldTypeAggregations,
  filters,
  convertTo,
}: TotalsRowProps) {
  const { timeFormat } = useOrgTimeFormat();
  const timezone = useDisplayTimezone();

  // Load and validate selections from localStorage
  const [selections, setSelections] = useState<Record<string, string>>(() => {
    const raw = loadSelectionsFromStorage(storageKey);
    return validateSelections(raw, schema, fieldTypeAggregations);
  });

  // Re-sync selections when storageKey changes (e.g. navigating to a different
  // entity type) or when schema/field-type data arrives asynchronously.
  // Loads exclusively from the new key — previous in-memory selections are
  // discarded to avoid leaking choices across entity types.
  useEffect(() => {
    const fromStorage = loadSelectionsFromStorage(storageKey);
    setSelections(
      validateSelections(fromStorage, schema, fieldTypeAggregations)
    );
  }, [storageKey, schema, fieldTypeAggregations]);

  // Build aggregations list for the API call
  const columnAggregations = useMemo(
    () =>
      Object.entries(selections).map(([propertyName, fn]) => ({
        propertyName,
        function: fn as AggregationFunction,
      })),
    [selections]
  );

  // Fetch aggregation values — uses placeholderData to keep previous
  // results visible during refetch, so only new columns show loading.
  const { data, isFetching, isError, error } = useColumnAggregations(
    orgId,
    entityTypeName,
    columnAggregations,
    filters,
    convertTo
  );

  // Handle aggregation selection change
  const handleSelect = useCallback(
    (propertyName: string, aggregation: string | null) => {
      setSelections(prev => {
        const next =
          aggregation === null
            ? Object.fromEntries(
                Object.entries(prev).filter(([k]) => k !== propertyName)
              )
            : { ...prev, [propertyName]: aggregation };
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // Storage full or unavailable — selections still work in-memory
        }
        return next;
      });
    },
    [storageKey]
  );

  if (pane === 'left') {
    return (
      <TotalsCell isPinned>
        {_leftColumns.length > 0 && (
          <span className='text-xs'>
            {rowCount.toLocaleString('en-US')} records
          </span>
        )}
      </TotalsCell>
    );
  }

  // pane === 'right' — AggregationPicker per property column
  return (
    <div
      className='flex items-stretch text-xs'
      style={{ minWidth: rightPaneContentWidth + addColumnExtraWidth }}
    >
      {rightColumns.map(col => {
        const colId = col.name ?? col.kind;
        const colWidth = columnWidths[colId] ?? 160;
        const propertyName = col.kind === 'property' ? col.name : null;
        const propSchema = propertyName ? schema[propertyName] : null;
        const allowedAggs = propSchema
          ? (fieldTypeAggregations[propSchema.field_type_name] ?? [])
          : [];
        const selected = propertyName
          ? (selections[propertyName] ?? null)
          : null;
        const alias =
          propertyName && selected ? `${propertyName}_${selected}` : null;
        const rawValue = alias ? (data?.aggregations?.[alias] ?? null) : null;
        const formattedValue =
          rawValue !== null
            ? formatAggregationValue(rawValue, timeFormat, timezone)
            : null;

        return (
          <TotalsCell
            key={colId}
            style={{ width: colWidth, minWidth: colWidth, flexShrink: 0 }}
          >
            {propertyName && allowedAggs.length > 0 ? (
              <AggregationPicker
                allowedAggregations={allowedAggs}
                selectedAggregation={selected}
                onSelect={agg => handleSelect(propertyName, agg)}
                isLoading={isFetching && selected !== null && rawValue === null}
                hasError={isError && selected !== null}
                error={error}
                value={formattedValue}
              />
            ) : null}
          </TotalsCell>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TotalsCell — individual cell in the totals row
// ---------------------------------------------------------------------------

interface TotalsCellProps {
  children?: React.ReactNode;
  isPinned?: boolean;
  style?: React.CSSProperties;
}

/**
 * TotalsCell wraps content in a flex cell for the totals row.
 * Pinned cells (left pane) have a slightly different appearance.
 */
export function TotalsCell({
  children,
  isPinned = false,
  style,
}: TotalsCellProps) {
  return (
    <div
      className={`flex items-center justify-end px-2 py-1.5 group relative${isPinned ? ' h-full font-medium text-[var(--color-text-secondary)]' : ''}`}
      style={style}
    >
      {children}
      {/* Vertical separator line — extends below the cell but clipped by
          the footer container's overflow-clip */}
      {!isPinned && (
        <span
          aria-hidden='true'
          className='absolute top-0 right-0 w-px bg-[var(--color-border-secondary)] pointer-events-none'
          style={{ bottom: '-100vh' }}
        />
      )}
    </div>
  );
}
